<template>
  <div class="min-h-full bg-transparent dark:bg-[#0B1120] text-gray-900 dark:text-[#F1F5F9] w-full max-w-full overflow-x-hidden">
    <!-- Desktop width constrained for a tighter, focused, professional layout -->
    <div
      class="w-full max-w-full lg:max-w-[1140px] xl:max-w-[1260px] mx-auto px-2 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6"
    >
      <Banner />

      <!-- Top Header & Segmented Tab Navigation (Admins only) -->
      <div
        v-if="userRole === 'admin'"
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6 mt-1"
      >
        <div class="min-w-0 flex-1">
          <h1 class="text-lg sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2 truncate">
            <CalendarClock class="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 dark:text-indigo-400 shrink-0" />
            <span class="truncate">Leave Management</span>
          </h1>
          <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5 truncate sm:whitespace-normal">
            Institutional leave approval pipeline & personal applications
          </p>
        </div>

        <!-- Segmented Tab Switcher: Full width on mobile, sleek inline bar on desktop -->
        <div
          class="bg-slate-100/90 dark:bg-[#18243A] p-1 rounded-xl border border-slate-200/80 dark:border-[#263449] flex items-center w-full sm:w-auto shadow-inner"
        >
          <button
            @click="currentView = 'management'"
            class="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-bold rounded-lg transition-all duration-200 select-none"
            :class="[
              currentView === 'management'
                ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/80 dark:border-[#202E44]'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
            ]"
          >
            <ShieldCheck class="w-4 h-4 shrink-0" />
            <span>Manage Requests</span>
            <span
              v-if="pendingCount > 0"
              class="px-1.5 py-0.5 rounded-full text-[10px] font-black bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400"
            >
              {{ pendingCount }}
            </span>
          </button>

          <button
            @click="currentView = 'application'"
            class="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-bold rounded-lg transition-all duration-200 select-none"
            :class="[
              currentView === 'application'
                ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/80 dark:border-[#202E44]'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
            ]"
          >
            <FilePlus class="w-4 h-4 shrink-0" />
            <span>Apply for Leave</span>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <LeaveApplication v-if="currentView === 'application'" />
      <LeaveManagement v-else-if="currentView === 'management'" />
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from "vue";
  import { useStore } from "vuex";
  import { CalendarClock, ShieldCheck, FilePlus } from "lucide-vue-next";
  import LeaveApplication from "@/components/LeaveRequest/ApplyLeave.vue";
  import LeaveManagement from "@/components/LeaveRequest/RequestCards.vue";
  import Banner from "@/components/Banner/TeacherBanner.vue";

  const store = useStore();
  const userRole = computed(() => store.getters.getUserRole);
  const currentView = ref("management");

  const leaveSummary = computed(
    () => store.getters.getLeaveSummary || store.state.leaveSummary
  );
  const pendingCount = computed(() => leaveSummary.value?.totalPending || 0);

  // If not admin, force the view to application
  onMounted(() => {
    if (userRole.value !== "admin") {
      currentView.value = "application";
    }
  });
</script>
